import React, { useState, useEffect } from 'react';
import { Grid, TextField, Typography } from '@material-ui/core';
import { Entity, UserEntity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { modelApiRef } from '../../../services';
import {
  DepartmentGroupEditRequest,
  GroupEditRequest,
  ProductGroupEditRequest,
} from '../../../services/ModelClient';
import { UserInput } from '../../input/UserInput';
import EnhancedTable from './EditGroupMembershipsUserTable';
import { DialogMergeRequest } from '../../dialogs';
import { MergeRequestResponse } from '../../dialogs/DialogMergeRequest';

/**
 * Whose group is being edited. A team is addressed by its uid and its short
 * id - the first names its directory in the CMDB repository, the second keys
 * it inside the file - while a department is addressed by its vuid alone,
 * which does both. A product needs the two of its team on top of its own short
 * key: its file sits in a directory of the team's, under the team's key.
 */
export type GroupMembershipTarget =
  | { kind: 'team'; cmdbTeamUID: string; cmdbTeamShortID: string }
  | { kind: 'department'; cmdbDepartmentVuid: string }
  | {
      kind: 'product';
      cmdbTeamUID: string;
      cmdbTeamShortID: string;
      cmdbProductKey: string;
    };

interface AddUserToADGroupDialogProps {
  openDialog: boolean;
  groupFullName: string;
  /**
   * The members the dialog edits, which for a department are its *declared*
   * members only - the ones it derives from its contacts are not editable
   * here and must not be written back, see DepartmentGroupEditRequest.
   */
  usersInGroup: UserEntity[];
  /**
   * The members the CMDB puts into the group by itself, today the contacts of
   * a department in its viewer group. They are shown so that the table is the
   * group as it will be, not a part of it that reads like the whole, but they
   * cannot be taken out here - the CMDB would put them straight back on the
   * next build - and they are left out of what is written.
   */
  derivedUsersInGroup?: UserEntity[];
  target: GroupMembershipTarget;
  cmdbGroupName: string;
  currentUserMail: string;
  cmdbGroupType: string;
  toggleDialog: () => void;
}

export enum UserStatus {
  KEEP,
  REMOVE,
  ADD,
  /** In the group, but not by anybody's hand - see `derivedUsersInGroup`. */
  DERIVED,
}

export interface UserChanges {
  user: UserEntity;
  status: UserStatus;
}

export function EditGroupMembershipsDialog(props: AddUserToADGroupDialogProps) {
  const defaultErrorMessageState = {
    requestMessage: { error: false, errorMessage: '' },
  };
  const {
    openDialog,
    cmdbGroupType,
    toggleDialog,
    usersInGroup,
    derivedUsersInGroup,
    cmdbGroupName,
    target,
    currentUserMail,
    groupFullName,
  } = props;
  const [autocompleteValue, setAutocompleteValue] = useState('');
  const [hasSameUsers, setHasSameUsers] = useState(true);
  // The declared members first, the way the CMDB keeps them in the file and
  // appends the derived ones behind.
  const userStuff = [
    ...usersInGroup.map(u => ({ user: u, status: UserStatus.KEEP })),
    ...(derivedUsersInGroup ?? []).map(u => ({
      user: u,
      status: UserStatus.DERIVED,
    })),
  ];
  const [usersToAdd, setUsersToAdd] = useState<UserChanges[]>(userStuff);
  const [requestMessage, setRequestMessage] = useState('');
  const [inputErrors, setInputErrors] = useState(defaultErrorMessageState);
  const modelApi = useApi(modelApiRef);
  const displayTitle = `Update group memberships of ${groupFullName}`;

  useEffect(() => {
    if (checkHasSameUsers()) {
      setHasSameUsers(true);
      return;
    }
    setHasSameUsers(false);
  }, [usersToAdd]);

  function handleUserToAddChange(val: Entity) {
    if (val == undefined) {
      return;
    }

    const nextUsersToAdd = [...usersToAdd];

    const userExists = nextUsersToAdd.filter(
      u =>
        u.user.spec.profile?.email?.toLowerCase() ===
        (val as UserEntity).spec.profile?.email?.toLowerCase(),
    );

    if (userExists.length < 1) {
      setUsersToAdd([
        { status: UserStatus.ADD, user: val as UserEntity },
        ...nextUsersToAdd,
      ]);
      return;
    }

    const user = userExists[0];

    if (user.status === UserStatus.REMOVE) {
      user.status = UserStatus.KEEP;
      setUsersToAdd(nextUsersToAdd);
      return;
    }
    setAutocompleteValue('');
  }

  function handleUserToAddRemove(u: UserChanges) {
    const nextUsersToAdd = [...usersToAdd];
    const userIndex = nextUsersToAdd.findIndex(
      a =>
        a.user.spec.profile?.email?.toLowerCase() ===
        u.user.spec.profile?.email?.toLowerCase(),
    );

    if (userIndex === -1) {
      return;
    }

    // Nobody takes a derived member out here, and the table offers no button
    // for it - the CMDB would put them back on the next build anyway.
    if (nextUsersToAdd[userIndex].status === UserStatus.DERIVED) {
      return;
    }

    if (nextUsersToAdd[userIndex].status === UserStatus.KEEP) {
      nextUsersToAdd[userIndex].status = UserStatus.REMOVE;
    } else if (nextUsersToAdd[userIndex].status === UserStatus.REMOVE) {
      nextUsersToAdd[userIndex].status = UserStatus.KEEP;
    } else if (nextUsersToAdd[userIndex].status === UserStatus.ADD) {
      // remove item from list again
      nextUsersToAdd.splice(userIndex, 1);
    }
    setUsersToAdd(nextUsersToAdd);
  }

  function inputsValid() {
    if (requestMessage.length === 0) {
      setInputErrors({
        ...inputErrors,
        requestMessage: {
          error: true,
          errorMessage: 'This is a required field. Please fill it out.',
        },
      });
      return false;
    }

    if (requestMessage.length > 1000) {
      setInputErrors({
        ...inputErrors,
        requestMessage: {
          error: true,
          errorMessage: 'The field may not exceed 1000 characters.',
        },
      });
      return false;
    }
    return true;
  }

  async function createMergeRequest() {
    const members = usersToAdd
      .filter(u => u.status === UserStatus.KEEP || u.status === UserStatus.ADD)
      .map(u => (u.user.spec.profile as any).userPrincipalName.toLowerCase());
    if (target.kind === 'department') {
      const editRequest: DepartmentGroupEditRequest = {
        cmdbGroupName: cmdbGroupName,
        cmdbGroupType: cmdbGroupType as any,
        cmdbDepartmentVuid: target.cmdbDepartmentVuid,
        members: members,
        requestMessage: requestMessage,
        requestingUser: currentUserMail,
      };
      return (await modelApi.requestDepartmentAdGroupUpdate(
        editRequest,
      )) as MergeRequestResponse;
    }
    if (target.kind === 'product') {
      // No category to state: `custom` is the only one a product has, see
      // ProductGroupEditRequest.
      const editRequest: ProductGroupEditRequest = {
        cmdbGroupName: cmdbGroupName,
        cmdbTeamUID: target.cmdbTeamUID,
        cmdbTeamShortId: target.cmdbTeamShortID,
        cmdbProductKey: target.cmdbProductKey,
        members: members,
        requestMessage: requestMessage,
        requestingUser: currentUserMail,
      };
      return (await modelApi.requestProductAdGroupUpdate(
        editRequest,
      )) as MergeRequestResponse;
    }
    const editRequest: GroupEditRequest = {
      cmdbGroupName: cmdbGroupName,
      cmdbGroupType: cmdbGroupType as any,
      cmdbTeamShortId: target.cmdbTeamShortID,
      cmdbTeamUID: target.cmdbTeamUID,
      members: members,
      requestMessage: requestMessage,
      requestingUser: currentUserMail,
    };
    return (await modelApi.requestAdGroupUpdate(
      editRequest,
    )) as MergeRequestResponse;
  }

  function handleTimeout() {
    setUsersToAdd(userStuff);
    setRequestMessage('');
    setInputErrors(defaultErrorMessageState);
  }

  function handleRequestMessageChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    // clear errors:
    setInputErrors({
      requestMessage: {
        error: false,
        errorMessage: '',
      },
    });

    setRequestMessage(event?.target.value);
  }

  function checkHasSameUsers(): boolean {
    // Only over what is written: a derived member is in both lists whatever
    // happens here, and counting it would make every dialog look changed.
    const currentMembers = userStuff
      .filter(user => user.status !== UserStatus.DERIVED)
      .map(user =>
        (user.user.spec.profile as any).userPrincipalName.toLowerCase(),
      );
    const newMembers = usersToAdd
      .filter(u => u.status === UserStatus.KEEP || u.status === UserStatus.ADD)
      .map(user =>
        (user.user.spec.profile as any).userPrincipalName.toLowerCase(),
      );

    return currentMembers.sort().join(',') === newMembers.sort().join(',');
  }

  const userTable = (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <UserInput
            fullWidth={true}
            onChange={handleUserToAddChange}
            value={autocompleteValue}
            usersToFilter={usersToAdd
              // A derived member is in the group as well, so the dropdown does
              // not offer them either - adding them would declare somebody the
              // department already has.
              .filter(user => user.status !== UserStatus.REMOVE)
              .map(user =>
                (user.user.spec.profile as any).userPrincipalName.toLowerCase(),
              )}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            error={inputErrors.requestMessage.error}
            id="outlined-helperText"
            label="Reason"
            helperText={
              inputErrors.requestMessage.error
                ? inputErrors.requestMessage.errorMessage
                : 'Please summarize your changes (mandatory)'
            }
            variant="outlined"
            FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
            margin="dense"
            fullWidth={true}
            value={requestMessage}
            onChange={handleRequestMessageChange}
          />
        </Grid>
        <Grid item xs={12}>
          {usersToAdd.length > 0 ? (
            <EnhancedTable
              usersToAdd={usersToAdd}
              deleteUser={handleUserToAddRemove}
            />
          ) : (
            <Typography align="center" variant="subtitle2">
              There are no users in this group yet. Please select a user from
              the above dropdown to add the first one.
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}></Grid>
      </Grid>
    </>
  );

  return (
    <DialogMergeRequest
      displayTitle={displayTitle}
      openDialog={openDialog}
      normalContent={userTable}
      hasChanges={!hasSameUsers}
      toggleDialog={toggleDialog}
      inputsValid={inputsValid}
      handleTimeout={handleTimeout}
      createMergeRequest={createMergeRequest}
    />
  );
}
